<template>
  <v-dialog
    :model-value="modelValue"
    width="600"
    :persistent="submitting"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card>
      <v-toolbar flat color="transparent">
        <v-toolbar-title
          >Vincular Loja a Software House/Revenda</v-toolbar-title
        >
      </v-toolbar>

      <v-card-text>
        <v-row v-if="store">
          <v-col cols="12">
            <strong>Loja:</strong> {{ store.loja_razao_social }}
          </v-col>
          <v-col cols="12">
            <strong>CNPJ:</strong> {{ formatCnpj(store.loja_cnpj) }}
          </v-col>
          <v-col cols="12">
            <strong>Dispositivos:</strong> {{ store.device_count }}
          </v-col>

          <v-col v-if="isAddingToExistingAssignment" cols="12">
            <v-alert density="compact" variant="outlined" color="grey">
              Adicionando <strong>{{ unassignedDeviceCount }}</strong>
              {{ unassignedDeviceCount === 1 ? "dispositivo" : "dispositivos" }}
              ao vinculo atual.
            </v-alert>
          </v-col>
          <v-col cols="12">
            <v-autocomplete
              v-model="selectedTenantId"
              :items="filteredTenants"
              :loading="tenantsLoading"
              item-title="razao_social"
              item-value="id"
              label="Selecionar Software House/Revenda"
              :disabled="isAddingToExistingAssignment"
              :rules="[(v) => !!v || 'Software House/Revenda e obrigatoria']"
            >
              <template v-slot:item="{ item, props }">
                <v-list-item v-bind="props">
                  <template v-slot:subtitle>
                    {{ formatCnpj(item.raw.cnpj) }}
                  </template>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col v-if="selectedTenantId" cols="12">
            <v-alert color="warning" density="compact" variant="outlined">
              Esta acao nao pode ser desfeita
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-btn
          size="large"
          @click="onCancel"
          :disabled="submitting"
          color="secondary"
        >
          Cancelar
        </v-btn>
        <v-spacer />
        <v-btn
          size="large"
          color="primary"
          @click="onConfirmAssign"
          :loading="submitting"
          :disabled="!selectedTenantId"
        >
          Vincular
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useCompaniesQuery } from "@/queries/modules/companies";
import { useAssignPreregistrationMutation } from "@/queries/modules/preregistrations";
import { useSnackbar } from "@/plugins/snackbar";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  store: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "success"]);

const $snackbar = useSnackbar();

// State
const selectedTenantId = ref(null);

// TanStack Query for tenants (companies)
const tenantsParams = computed(() => ({
  page: 1,
  per_page: 1000,
}));

const { data: tenantsData, isLoading: tenantsLoading } =
  useCompaniesQuery(tenantsParams);

const tenants = computed(() => tenantsData.value?.items || []);

// Mutation for assigning pre-registration
const assignMutation = useAssignPreregistrationMutation();
const submitting = computed(() => assignMutation.isPending.value);

// Computed properties
const isAddingToExistingAssignment = computed(() => {
  if (!props.store || !props.store.devices) {
    return false;
  }
  return props.store.devices.some((device) => device.assigned === true);
});

const existingTenantInfo = computed(() => {
  if (!props.store || !props.store.devices) {
    return null;
  }
  const assignedDevice = props.store.devices.find(
    (device) => device.assigned === true,
  );
  if (!assignedDevice) {
    return null;
  }
  return {
    tenant_id: assignedDevice.tenant_id,
  };
});

const unassignedDeviceCount = computed(() => {
  if (!props.store || !props.store.devices) {
    return 0;
  }
  return props.store.devices.filter((device) => device.assigned === false)
    .length;
});

const filteredTenants = computed(() => {
  if (
    isAddingToExistingAssignment.value &&
    existingTenantInfo.value &&
    existingTenantInfo.value.tenant_id
  ) {
    return tenants.value.filter(
      (tenant) => tenant.id === existingTenantInfo.value.tenant_id,
    );
  }
  return tenants.value;
});

// Format CNPJ helper
const formatCnpj = (cnpj) => {
  if (!cnpj) return "";
  const cleaned = cnpj.replace(/\D/g, "");
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
};

// Methods
const onConfirmAssign = async () => {
  try {
    const result = await assignMutation.mutateAsync({
      loja_cnpj: props.store.loja_cnpj,
      tenant_id: selectedTenantId.value,
    });
    $snackbar.success(
      `${result.updated_count} dispositivos vinculados a ${result.tenant_name}`,
    );
    emit("success");
    emit("update:modelValue", false);
  } catch (e) {
    $snackbar.error(e.message);
  }
};

const onCancel = () => {
  emit("update:modelValue", false);
};

// Pre-select existing tenant if adding to existing assignment
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      nextTick(() => {
        if (existingTenantInfo.value && existingTenantInfo.value.tenant_id) {
          selectedTenantId.value = existingTenantInfo.value.tenant_id;
        } else {
          selectedTenantId.value = null;
        }
      });
    }
  },
  { immediate: true },
);
</script>
